import { createContext, useContext, type ReactNode } from "react"

/**
 * Maps `${simulationOutputId}::${metricId}` to the email of the user who
 * actually owns the underlying database row backing the merged annotation
 * currently displayed in the AnnotationTable. Used by `useHumanFeedbackUpdate`
 * to keep its "is this my row?" check honest in collaborative projects, where
 * the merged annotation may be displayed under the current user's identity for
 * UX continuity but actually belongs to a different reviewer.
 */
export type MergedAnnotationOwnerMap = Record<string, string | null>

const MergedAnnotationOwnerContext =
  createContext<MergedAnnotationOwnerMap | null>(null)

export const mergedAnnotationOwnerKey = (
  simulationOutputId: string,
  metricId: string,
): string => `${simulationOutputId}::${metricId}`

export const MergedAnnotationOwnerProvider: React.FC<{
  value: MergedAnnotationOwnerMap
  children: ReactNode
}> = ({ value, children }) => (
  <MergedAnnotationOwnerContext.Provider value={value}>
    {children}
  </MergedAnnotationOwnerContext.Provider>
)

export const useMergedAnnotationOwner = (
  simulationOutputId: string,
  metricId: string,
): string | null | undefined => {
  const map = useContext(MergedAnnotationOwnerContext)
  if (!map) {
    return undefined
  }
  return map[mergedAnnotationOwnerKey(simulationOutputId, metricId)]
}
